import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal lollipop chart is a variation of the bar chart where data points are
          represented by a horizontal line with a circular marker at the end. The length of the line
          reflects the value, and the circle emphasizes the data point. This chart is useful for
          comparing values across categories, especially when category labels are long or when a
          cleaner, more visually appealing design is desired.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal lollipop charts are commonly used for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Highlighting Precise Data Values:</strong> They emphasize specific values,
            making them ideal for visualizing metrics like satisfaction ratings or productivity
            scores, where clarity of the endpoint is key.
          </li>
          <li>
            <strong>Facilitating Comparisons Across Categories:</strong> The horizontal orientation
            is particularly useful for comparing values when category labels are lengthy, such as
            employee names or survey questions.
          </li>
          <li>
            <strong>Minimizing Visual Clutter:</strong> Horizontal lollipop charts offer a clean and
            modern design, ideal for presentations or reports that require simplicity while
            retaining focus on key data points.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal lollipop charts are a useful tool for visualizing data across categories, but
          they come with certain limitations that should be taken into account:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Long Category Labels:</strong> When the category names are
            long, horizontal lollipop charts can become hard to read as the labels may overlap or
            take up too much space, reducing the clarity of the visualization.
          </li>
          <li>
            <strong>Ineffective for Large Data Sets:</strong> Similar to other chart types,
            horizontal lollipop charts may struggle with large datasets. When there are too many
            categories, the chart can become crowded, making it difficult to distinguish between
            data points.
          </li>
          <li>
            <strong>Challenges in Showing Relationships Between Multiple Variables:</strong>{' '}
            Horizontal lollipop charts work best for comparing a single variable across categories.
            When trying to visualize multiple variables or complex relationships, other chart types,
            like stacked bar charts, may be more effective.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For horizontal lollipop charts, color selection is key to maintaining clarity and visual
          appeal. Distinct, contrasting colors help differentiate between categories, making it
          easier to compare values across the chart. Using subtle gradients can convey trends or
          variations in magnitude, guiding the viewer&apos;s attention to important data points.
          Thoughtful color choices also prevent the chart from becoming visually cluttered, ensuring
          that each data series is easily distinguishable and the overall message is clear.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal lollipop charts are a useful visualization tool, but they present several
          challenges that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Data Points:</strong> Horizontal lollipop charts can face
            challenges when there are many data points. As the chart’s width is limited, labels and
            lollipop markers may overlap, making it harder to distinguish between individual data
            points, especially when the values are similar.
          </li>
          <li>
            <strong>Difficulty in Comparing Multiple Data Series:</strong> When comparing multiple
            series in a horizontal lollipop chart, the spacing between lollipop markers becomes
            crucial. If the data points for each series are too close together, it can be
            challenging to compare the values accurately, and the chart may lose clarity.
          </li>
          <li>
            <strong>Challenges with Small or Similar Data Differences:</strong> In horizontal
            lollipop charts, small differences between data points can be hard to represent clearly,
            especially if the chart has a wide range of values. The length of the lollipop markers
            might not be easily distinguishable if the differences are minimal, leading to potential
            misinterpretation of the data.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
